// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — one round of a live race, decoupled from Trystero, the DOM and the clock
// (driven by place()/tick(), which is what makes the host-transfer contract testable
// without a network — tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer holds its own copy of the identical letter stream
// (a pure function of the frozen seed) and fills its own grid independently. NO game
// state crosses the wire during play — only a tiny status gossip (score, placed, done),
// plus the final grid ONCE on finishing so the results can show everyone's breakdown
// (principle #9). Each peer reaches its OWN game-over (grid full / round timer) with no
// host help, so HOST TRANSFER IS A PURE DISPLAY CONCERN. onHostChange is wired anyway.

import { createGame, place as applyPlace, tickTimer, score as scoreOf, type GameState, type Reason } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  score: number;
  placed: number;
  over: boolean;
  left: boolean;
  /** The final grid, for the results reveal (arrives on finish). */
  cells: (string | null)[] | null;
  reason: Reason;
}

/** Wire status. Tiny; the final grid rides only on the finishing message. */
export interface RaceMsg {
  s: number; // score
  p: number; // placed
  d: boolean; // done
  g?: (string | null)[]; // final grid (on finish only)
  r?: Reason; // reason (on finish only)
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  timed: boolean;
  deps: RaceDeps;
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed, o.timed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        score: 0,
        placed: 0,
        over: false,
        left: false,
        cells: null,
        reason: null,
      });
    });
    if (o.selfSeat !== null) this.syncSelf();
  }

  get isHost(): boolean {
    return this.host;
  }
  get msLeft(): number {
    return this.state.msLeft;
  }
  get selfScore(): number {
    return this.self()?.score ?? 0;
  }

  private self(): PeerResult | undefined {
    return this.selfSeat === null ? undefined : this.results.get(this.selfSeat);
  }

  private syncSelf(): void {
    const r = this.self();
    if (!r) return;
    r.score = scoreOf(this.state, this.mode).total;
    r.placed = this.state.placed;
    r.over = this.state.over;
    if (this.state.over) {
      r.cells = this.state.cells.slice();
      r.reason = this.state.reason;
    }
  }

  /** Place a local letter; returns whether it changed anything. */
  place(cellIdx: number, queueIdx = 0): boolean {
    if (this.selfSeat === null || this.state.over) return false;
    const next = applyPlace(this.state, cellIdx, queueIdx);
    if (next === this.state) return false;
    this.state = next;
    this.syncSelf();
    if (this.state.over) return this.finishSelf();
    this.broadcastProgress();
    this.deps.onChange();
    return true;
  }

  private broadcastProgress(): void {
    const r = this.self();
    if (!r) return;
    this.deps.send({ s: r.score, p: r.placed, d: false });
  }

  private finishSelf(): boolean {
    if (this.ended) return true;
    this.ended = true;
    this.syncSelf();
    const r = this.self();
    if (r) this.deps.send({ s: r.score, p: r.placed, d: true, g: r.cells ?? undefined, r: r.reason });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
    return true;
  }

  /** A status update from another peer. */
  receive(msg: RaceMsg, fromSeat: number): void {
    const r = this.results.get(fromSeat);
    if (!r || r.seat === this.selfSeat) return;
    r.score = msg.s;
    r.placed = Math.max(r.placed, msg.p);
    if (msg.d) {
      r.over = true;
      if (Array.isArray(msg.g)) r.cells = msg.g;
      if (msg.r !== undefined) r.reason = msg.r;
    }
    this.deps.onChange();
  }

  /** Interval-driven clock (never rAF alone). Every seated peer runs its own. */
  tick(dtMs: number): void {
    if (this.selfSeat === null || this.state.over) return;
    const next = tickTimer(this.state, dtMs);
    if (next === this.state) return;
    this.state = next;
    this.syncSelf();
    if (this.state.over) this.finishSelf();
    else this.deps.onChange();
  }

  /** Promotion carries no authoritative duty here — recorded for display only. */
  setHost(isHost: boolean): void {
    this.host = isHost;
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (!r || seat === this.selfSeat) return;
    r.left = true;
    r.over = true; // a departed peer will never finish; don't hold the room for it
    this.deps.onChange();
  }
  peerReturned(seat: number): void {
    const r = this.results.get(seat);
    if (r) r.left = false;
  }

  everyoneDone(): boolean {
    return [...this.results.values()].every((r) => r.over);
  }

  /** Results best-first: highest score wins, departed peers last. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      return b.score - a.score;
    });
  }
}

/** Seat index from the frozen roster, or null for a spectator / unknown peer. */
export function seatOf(roster: Array<{ id: string }>, peerId: string): number | null {
  const i = roster.findIndex((p) => p.id === peerId);
  return i < 0 ? null : i;
}

/** A one-shot timer safe to poke repeatedly — re-arming on every poll starves it. */
export function createOneShot(delayMs: number, run: () => void) {
  let id: ReturnType<typeof setTimeout> | null = null;
  return {
    poke(): void {
      if (id !== null) return;
      id = setTimeout(() => {
        id = null;
        run();
      }, delayMs);
    },
    cancel(): void {
      if (id !== null) clearTimeout(id);
      id = null;
    },
  };
}
